import {
  formatEventTimeLabel,
  getShowDateTimeParts,
  parseCmsAdelaideCalendarDate,
  parseShowCalendarDate,
  parseShowDateTimeInstant,
  sameShowCalendarDay,
  SHOW_DATE_LOCALE,
} from "./showDates";

export { dayUntil } from "./dayUntil";

export function getEventsByDay(events, selectedDay, showDates) {
  if (selectedDay.slug == "all-days") return events;

  return events?.filter((event) => {
    if (event?.every_day) {
      return true;
    }

    if (event?.multi_day) {
      return event.days.some((day) =>
        sameShowCalendarDay(day.date, selectedDay.date)
      );
    }

    const eventStartDate = parseShowCalendarDate(event.start_date);
    const eventEndDate = parseShowCalendarDate(event.end_date);
    const selectedDate = parseShowCalendarDate(selectedDay.date);

    if (!eventStartDate || !eventEndDate || !selectedDate) {
      return false;
    }

    return selectedDate >= eventStartDate && selectedDate <= eventEndDate;
  });
}

export function getTime(collectionHandle, data, selectedDay) {
  if (collectionHandle != "events") return false;

  if (selectedDay.slug == "all-days") return false;
}

export function getTiming(daysUntil, message = "entries open!") {
  if (!daysUntil) {
    return null;
  }

  if (daysUntil.days > 0) {
    return (
      <p className="leading-none level-4">
        Day{daysUntil?.days == 1 ? "" : "s"} until <br />
        {message}{" "}
      </p>
    );
  } else if (daysUntil.hours > 0) {
    return (
      <p className="leading-none level-4">
        Hour{daysUntil.hours == 1 ? "" : "s"} until <br />
        {message}{" "}
      </p>
    );
  } else if (daysUntil.minutes > 0) {
    return (
      <p className="leading-none level-4">
        Minute{daysUntil.minutes == 1 ? "" : "s"} until <br />
        {message}{" "}
      </p>
    );
  } else if (daysUntil.seconds > 0) {
    return (
      <p className="leading-none level-4">
        Second{daysUntil.seconds == 1 ? "" : "s"} until <br />
        {message}{" "}
      </p>
    );
  }
}

export function getAmount(daysUntil) {
  if (daysUntil.days > 0) {
    return daysUntil.days;
  } else if (daysUntil.hours > 0) {
    return daysUntil.hours;
  } else if (daysUntil.minutes > 0) {
    return daysUntil.minutes;
  } else if (daysUntil.seconds > 0) {
    return daysUntil.seconds;
  }
}

/**
 * Human-readable calendar date for competition copy, e.g. "26th of May 2026"
 * or with `{ includeTime: true }`: "7th of August 2026 at 5:00 PM".
 * @param {string|Date|undefined|null} dateInput
 * @param {{ includeTime?: boolean }} [options]
 * @returns {string|null}
 */
export function formatOrdinalDateEnAU(dateInput, options = {}) {
  if (dateInput == null || dateInput === "") return null;

  const date =
    parseCmsAdelaideCalendarDate(dateInput) ?? parseShowCalendarDate(dateInput);
  if (!date) return null;

  const y = date.getFullYear();
  const monthIndex = date.getMonth();
  const day = date.getDate();

  const month = new Date(y, monthIndex, 1).toLocaleDateString(SHOW_DATE_LOCALE, {
    month: "long",
  });

  const suffix =
    day === 11 || day === 12 || day === 13
      ? "th"
      : day % 10 === 1
        ? "st"
        : day % 10 === 2
          ? "nd"
          : day % 10 === 3
            ? "rd"
            : "th";

  let label = `${day}${suffix} of ${month} ${y}`;

  if (options.includeTime) {
    const instant = parseShowDateTimeInstant(dateInput);
    if (instant) {
      const parts = getShowDateTimeParts(instant);
      if (parts.hour !== 0 || parts.minute !== 0) {
        const timeLabel = formatEventTimeLabel(
          `${parts.hour}:${String(parts.minute).padStart(2, "0")}`
        );
        if (timeLabel && timeLabel !== "-") {
          label = `${label} at ${timeLabel}`;
        }
      }
    }
  }

  return label;
}

export function getTimeIn12HourFormat(time) {
  const [hour, minute] = time.split(":");
  const hourIn12HourFormat = hour % 12 || 12;
  const period = hour < 12 ? "AM" : "PM";
  return `${hourIn12HourFormat}:${minute} ${period}`;
}

export function getMapCategories(array1, array2) {
  const categories = [];

  array1.forEach((item) => {
    item.categories?.forEach((category) => {
      if (!categories.some((cat) => cat.slug === category.slug)) {
        categories.push(category);
      }
    });
  });

  array2.forEach((item) => {
    item.categories?.forEach((category) => {
      if (!categories.some((cat) => cat.slug === category.slug)) {
        categories.push(category);
      }
    });
  });

  return categories;
}

export function getFilteredLocations(locations, activeCategory, searchWord) {
  if (activeCategory === "all" && !searchWord) return locations;

  if (activeCategory === "all" && searchWord) {
    return locations.filter((location) => {
      if (location.isFacility) {
        return location?.facilities?.some((facility) =>
          facility?.title?.toLowerCase().includes(searchWord.toLowerCase())
        );
      }
      return location?.title?.toLowerCase().includes(searchWord.toLowerCase());
    });
  } else {
    return locations.filter((location) => {
      if (location.isFacility) {
        return location?.facilities?.some((facility) =>
          facility?.categories?.some(
            (category) => category?.slug === activeCategory
          )
        );
      }

      return location?.categories?.some(
        (category) => category?.slug === activeCategory
      );
    });
  }
}
